import { useState, useCallback, useEffect } from "react";

const defaultChartFilters = {
  industries: [] as string[],
  ceoGenders: [] as string[],
  cantons: [] as string[],
  phases: [] as string[],
};

type YearRange = [number, number];

export interface GlobalFiltersState {
  yearRange: YearRange;
}

export interface ChartFiltersState {
  timeline: typeof defaultChartFilters;
  industry: typeof defaultChartFilters;
  phase: typeof defaultChartFilters;
  canton: typeof defaultChartFilters;
  ceoGender: typeof defaultChartFilters;
}

export const useDashboardFilters = (initialYearRange: YearRange) => {
  const [globalFilters, setGlobalFilters] = useState<GlobalFiltersState>({
    yearRange: initialYearRange,
  });

  const [chartFilters, setChartFilters] = useState<ChartFiltersState>({
    timeline: { ...defaultChartFilters },
    industry: { ...defaultChartFilters },
    phase: { ...defaultChartFilters },
    canton: { ...defaultChartFilters },
    ceoGender: { ...defaultChartFilters },
  });

  useEffect(() => {
    setGlobalFilters((prev) => {
      if (prev.yearRange[0] === initialYearRange[0] && prev.yearRange[1] === initialYearRange[1]) {
        return prev;
      }
      return { ...prev, yearRange: initialYearRange };
    });
  }, [initialYearRange]);

  const updateFilter = useCallback(
    (
      key: keyof GlobalFiltersState | keyof typeof defaultChartFilters,
      value: any,
      chartKey?: keyof ChartFiltersState,
      activeChart?: keyof ChartFiltersState
    ) => {
      // Note: `updateFilter` is used for both global filters (yearRange) and
      // per-chart filters. When updating array filters we pass the target chart
      // key (or activeChart) so mobile and desktop filter states can differ.
      if (key === "yearRange") {
        setGlobalFilters((prev) => ({ ...prev, yearRange: value as YearRange }));
        return;
      }
      const chart = (chartKey || activeChart) as keyof ChartFiltersState;
      setChartFilters((prev) => ({
        ...prev,
        [chart]: { ...(prev[chart] || {}), [key]: value },
      }));
    },
    []
  );

  const toggleArrayFilter = useCallback(
    (
      key: keyof typeof defaultChartFilters,
      value: string,
      chartKey?: keyof ChartFiltersState,
      activeChart?: keyof ChartFiltersState
    ) => {
      const chart = (chartKey || activeChart) as keyof ChartFiltersState;
      setChartFilters((prev) => {
        const arr = (prev[chart] as any)?.[key] || [];
        const next = arr.includes(value) ? arr.filter((i: string) => i !== value) : [...arr, value];
        return { ...prev, [chart]: { ...(prev[chart] || {}), [key]: next } } as ChartFiltersState;
      });
    },
    []
  );

  const resetFilters = useCallback(
    (chartKey?: keyof ChartFiltersState, activeChart?: keyof ChartFiltersState) => {
      const chart = (chartKey || activeChart) as keyof ChartFiltersState;
      setChartFilters((prev) => ({ ...prev, [chart]: { ...defaultChartFilters } }));
    },
    []
  );

  return {
    globalFilters,
    chartFilters,
    updateFilter,
    toggleArrayFilter,
    resetFilters,
  } as const;
};
